import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

export type ActionIntent = 'primary' | 'neutral' | 'danger';

export type GridAction<T> = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  intent?: ActionIntent;
  /** always render as an inline button next to ⋮, never inside the dropdown */
  sticky?: boolean;
  /** when true the sticky button stays fully visible (use to indicate active/on state) */
  isActive?: (row: T) => boolean;
  /** show only if predicate passes */
  isVisible?: (row?: T) => boolean;
  /** disable if predicate passes */
  isDisabled?: (row?: T) => boolean | string; // string = tooltip reason
  /** confirm with a modal before executing */
  requiresConfirm?: (row?: T) =>
    | false
    | {
        title: string;
        body?: string;
        confirmText?: string;
        pendingText?: string;
      };
  /** execute for a single row */
  run?: (row: T) => Promise<void> | void;
  /** execute for many rows (bulk) */
  runBulk?: (rows: T[]) => Promise<void> | void;
};

export function useGridActionController<T>(actions: GridAction<T>[]) {
  const [confirmState, setConfirmState] = useState<{
    action?: GridAction<T>;
    row?: T;
    params?: {
      title: string;
      body?: string;
      confirmText?: string;
      pendingText?: string;
    };
  } | null>(null);

  const visibleRowActions = useCallback(
    (row: T) =>
      actions.filter(a => (a.isVisible ? a.isVisible(row) : true) && a.run),
    [actions]
  );

  const visibleBulkActions = useCallback(
    (rows: T[]) =>
      actions.filter(
        a =>
          (a.isVisible ? a.isVisible(undefined) : true) &&
          a.runBulk &&
          rows.length > 0
      ),
    [actions]
  );

  const execute = useCallback(async (action: GridAction<T>, row: T) => {
    const confirm = action.requiresConfirm?.(row);
    if (confirm) {
      setConfirmState({ action, row, params: confirm });
      return;
    }
    try {
      await action.run?.(row);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast.error(e?.message ?? 'Action failed');
    }
  }, []);

  const executeBulk = useCallback(async (action: GridAction<T>, rows: T[]) => {
    const confirm = action.requiresConfirm?.(undefined);
    if (confirm) {
      setConfirmState({ action, params: confirm });
      // store rows implicitly—simple version uses closure
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (executeBulk as any)._pendingRows = rows;
      return;
    }
    try {
      await action.runBulk?.(rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast.error(e?.message ?? 'Action failed');
    }
  }, []);

  const confirmProceed = useCallback(async () => {
    if (!confirmState?.action) return;
    try {
      if (confirmState.row) {
        await confirmState.action.run?.(confirmState.row);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } else if ((executeBulk as any)._pendingRows) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rows = (executeBulk as any)._pendingRows as T[];
        await confirmState.action.runBulk?.(rows);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (executeBulk as any)._pendingRows = undefined;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast.error(e?.message ?? 'Action failed');
    } finally {
      setConfirmState(null);
    }
  }, [confirmState]);

  const cancelConfirm = () => setConfirmState(null);

  return {
    visibleRowActions,
    visibleBulkActions,
    execute,
    executeBulk,
    confirmState,
    confirmProceed,
    cancelConfirm
  };
}
